import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PER_PAGE, Pager } from "@/components/Pager";
import { api, downloadFile, errorMessage } from "@/lib/api";
import { formatDate, money } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";

const BUCKETS = ["current", "d1_30", "d31_60", "d61_90", "d90_plus"];

export default function Reports() {
  const { currency } = useAuth();
  const [view, setView] = useState("ledger");
  const [groupBy, setGroupBy] = useState("firm");
  const [ledger, setLedger] = useState(null);
  const [aging, setAging] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [agingPage, setAgingPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ledgerRes, agingRes] = await Promise.all([
        api.get("/reports/ledger", { params: { group_by: groupBy } }),
        api.get("/reports/aging"),
      ]);
      setLedger(ledgerRes.data);
      setAging(agingRes.data);
    } catch (error) {
      toast.error(errorMessage(error, "Could not load reports"));
    } finally {
      setLoading(false);
    }
  }, [groupBy]);

  useEffect(() => {
    load();
  }, [load]);

  const exportLedger = async () => {
    try {
      await downloadFile(`/exports/ledger.csv?group_by=${groupBy}`, `ledger_by_${groupBy}.csv`);
      toast.success("Ledger exported");
    } catch (error) {
      toast.error(errorMessage(error, "Export failed"));
    }
  };

  return (
    <div className="space-y-8" data-testid="reports-page">
      <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="label-caps">Accounts</p>
          <h1 className="mt-3 font-serif text-3xl tracking-tight text-slate-900 lg:text-4xl">
            Ledger &amp; aging
          </h1>
          <p className="mt-3 max-w-xl text-sm text-slate-600">
            Receivables grouped by law firm, individual instructing lawyer or matter, with a
            bucketed aging view of everything unpaid.
          </p>
        </div>
        <button
          type="button"
          onClick={exportLedger}
          data-testid="export-ledger-button"
          className="inline-flex items-center gap-2 border border-slate-300 bg-white px-5 py-2.5 text-[13px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
        >
          <Download className="h-3.5 w-3.5" strokeWidth={1.8} />
          Export ledger CSV
        </button>
      </header>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Tabs value={view} onValueChange={setView}>
          <TabsList className="rounded-sm">
            <TabsTrigger value="ledger" className="rounded-sm" data-testid="tab-ledger">
              Ledger
            </TabsTrigger>
            <TabsTrigger value="aging" className="rounded-sm" data-testid="tab-aging">
              Aging detail
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {view === "ledger" && (
          <Tabs value={groupBy} onValueChange={setGroupBy}>
            <TabsList className="rounded-sm">
              <TabsTrigger value="firm" className="rounded-sm" data-testid="group-by-firm">
                By law firm
              </TabsTrigger>
              <TabsTrigger value="contact" className="rounded-sm" data-testid="group-by-contact">
                By lawyer
              </TabsTrigger>
              <TabsTrigger value="case" className="rounded-sm" data-testid="group-by-case">
                By matter
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : view === "ledger" ? (
        ledger?.rows?.length ? (
          <div className="surface overflow-x-auto" data-testid="ledger-table">
            <table className="w-full min-w-[980px] text-left">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="label-caps px-5 py-3">Account</th>
                  <th className="label-caps px-5 py-3 text-right">Invoices</th>
                  <th className="label-caps px-5 py-3 text-right">Billed</th>
                  <th className="label-caps px-5 py-3 text-right">Collected</th>
                  <th className="label-caps px-5 py-3 text-right">Outstanding</th>
                  <th className="label-caps px-5 py-3 text-right">Overdue</th>
                  {BUCKETS.map((b) => (
                    <th key={b} className="label-caps px-4 py-3 text-right">
                      {ledger.bucket_labels[b]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {ledger.rows.slice((ledgerPage - 1) * PER_PAGE, ledgerPage * PER_PAGE).map((row) => (
                  <tr key={row.key} data-testid={`ledger-row-${row.key}`}>
                    <td className="max-w-[260px] px-5 py-4">
                      <p className="truncate text-[13px] font-semibold text-slate-900">
                        {row.name}
                      </p>
                      <p className="truncate text-[11px] text-slate-400">{row.subtitle}</p>
                    </td>
                    <td className="num px-5 py-4 text-right text-[12px] text-slate-600">
                      {row.invoice_count}
                    </td>
                    <td className="num px-5 py-4 text-right text-[12px] text-slate-800">
                      {money(row.billed, currency)}
                    </td>
                    <td className="num px-5 py-4 text-right text-[12px] text-emerald-700">
                      {money(row.collected, currency)}
                    </td>
                    <td className="num px-5 py-4 text-right text-[13px] font-semibold text-slate-900">
                      {money(row.outstanding, currency)}
                    </td>
                    <td className="num px-5 py-4 text-right text-[12px] text-red-600">
                      {money(row.overdue, currency)}
                    </td>
                    {BUCKETS.map((b) => (
                      <td key={b} className="num px-4 py-4 text-right text-[12px] text-slate-500">
                        {row.buckets[b] ? money(row.buckets[b], currency) : "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-5 pb-5 pt-2">
              <Pager
                page={ledgerPage}
                total={ledger.rows.length}
                onPage={setLedgerPage}
                testId="ledger-pager"
              />
            </div>
          </div>
        ) : (
          <div className="surface p-10 text-center" data-testid="ledger-empty">
            <p className="text-sm text-slate-600">Nothing to report yet.</p>
          </div>
        )
      ) : aging?.rows?.length ? (
        <div className="surface overflow-x-auto" data-testid="aging-table">
          <table className="w-full min-w-[900px] text-left">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {["Invoice", "Matter", "Billed to", "Due", "Days overdue", "Bucket", "Balance", "Status"].map(
                  (h) => (
                    <th
                      key={h}
                      className={`label-caps px-5 py-3 ${h === "Balance" || h === "Days overdue" ? "text-right" : ""}`}
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {aging.rows.slice((agingPage - 1) * PER_PAGE, agingPage * PER_PAGE).map((row) => (
                <tr key={row.invoice_id} data-testid={`aging-row-${row.invoice_number}`}>
                  <td className="px-5 py-4">
                    <Link
                      to={`/invoices/${row.invoice_id}`}
                      className="num text-[13px] text-slate-900 underline-offset-4 hover:underline"
                    >
                      {row.invoice_number}
                    </Link>
                    <p className="num text-[10px] uppercase text-slate-400">{row.matter_ref}</p>
                  </td>
                  <td className="max-w-[240px] px-5 py-4">
                    <p className="truncate text-[13px] text-slate-800">{row.case_title}</p>
                  </td>
                  <td className="max-w-[220px] px-5 py-4">
                    <p className="truncate text-[13px] text-slate-800">{row.contact_name}</p>
                    <p className="truncate text-[11px] text-slate-400">
                      {row.firm_name || "Direct client"}
                    </p>
                  </td>
                  <td className="num px-5 py-4 text-[12px] text-slate-600">
                    {formatDate(row.due_date)}
                  </td>
                  <td className="num px-5 py-4 text-right text-[12px] text-slate-900">
                    {row.days_overdue || "—"}
                  </td>
                  <td className="px-5 py-4 text-[12px] text-slate-600">{row.bucket}</td>
                  <td className="num px-5 py-4 text-right text-[13px] text-slate-900">
                    {money(row.balance ?? row.total, currency)}
                    {Number(row.amount_paid) > 0 ? (
                      <span className="block text-[10px] font-normal text-slate-400">
                        of {money(row.total, currency)}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-5 py-4">
                    <StatusBadge status={row.display_status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-5 pb-5 pt-2">
            <Pager
              page={agingPage}
              total={aging.rows.length}
              onPage={setAgingPage}
              testId="aging-pager"
            />
          </div>
        </div>
      ) : (
        <div className="surface p-10 text-center" data-testid="aging-empty">
          <p className="text-sm text-slate-600">No unpaid invoices. Books are clear.</p>
        </div>
      )}
    </div>
  );
}
